import { isNative } from '../platform.js';
import { stripeProvider } from './stripeProvider.js';

/**
 * Seleciona como uma assinatura é comprada, conforme a plataforma.
 *
 * Motivo desta abstração (App Store Guideline 3.1.1): conteúdo digital
 * consumido dentro do app iOS PRECISA ser vendido por In-App Purchase. O
 * checkout do Stripe — que é o fluxo da web — não pode ser montado no build
 * nativo, e o app também não pode conter link, botão ou texto direcionando o
 * usuário a comprar fora dele. Descumprir isso é a principal causa de
 * rejeição na revisão da Apple.
 *
 * Contrato que todo provider implementa:
 *   getKind()          -> 'stripe' | 'revenuecat'
 *   canPurchase()      -> boolean   (false = a UI não deve oferecer compra)
 *   listOfferings()    -> Promise<Array<{planId, priceLabel, period, productId}>>
 *   purchase(plan, {billingCycle}) -> Promise<{success, kind, clientSecret?, planId?}>
 *   restorePurchases() -> Promise<{restored: boolean, planId?}>
 *
 * `purchase()` propositalmente NÃO navega nem abre UI: devolve o que o
 * chamador precisa para seguir (no Stripe, o `clientSecret` do checkout
 * embedded; no RevenueCat, o resultado da compra já concluída pela loja).
 * Assim o PlanGrid continua dono da apresentação.
 */

let _revenuecatProvider = null;

// Import dinâmico: o pacote do RevenueCat só existe no build nativo, e mesmo
// lá ele só é carregado quando alguém tenta comprar — mantém o bundle web
// livre de código de loja (mesmo espírito de platform.js/tokenStore.js).
async function _getRevenueCatProvider() {
    if (!_revenuecatProvider) {
        const mod = await import('./revenuecatProvider.js');
        _revenuecatProvider = mod.revenuecatProvider;
    }
    return _revenuecatProvider;
}

export async function getPurchaseProvider() {
    if (isNative()) return _getRevenueCatProvider();
    return stripeProvider;
}

/**
 * Versão síncrona, para a UI decidir o que renderizar sem esperar import.
 * Só responde "que tipo de cobrança esta plataforma usa", nunca compra.
 */
export function getPurchaseKind() {
    return isNative() ? 'revenuecat' : 'stripe';
}

/**
 * A UI deve oferecer compra nesta plataforma agora?
 *
 * No nativo isto é `false` enquanto o RevenueCat não estiver configurado
 * (chaves + produtos criados no App Store Connect / Play Console). Enquanto
 * for `false`, a tela de planos mostra os planos como informação, sem
 * qualquer CTA de compra — que é justamente o que a Guideline 3.1.1 exige
 * quando não há IAP disponível.
 */
export function canOfferPurchase() {
    if (!isNative()) return true;
    return Boolean(
        import.meta.env.VITE_REVENUECAT_IOS_KEY || import.meta.env.VITE_REVENUECAT_ANDROID_KEY
    );
}
